/**
 * Runtime config. `VITE_USE_MOCKS=true` swaps the whole data layer to the in-memory mocks (useful
 * for local dev / tests / storybook-less previews without a backend). Default = live `/api` client.
 * Tests force mocks on so they never hit the network.
 */
export const USE_MOCKS: boolean =
  import.meta.env.VITE_USE_MOCKS === "true" ||
  // Vitest / jsdom: no real backend — always mock.
  (typeof process !== "undefined" && process.env?.VITEST === "true");

/** Cognito config for Amplify Auth (deployed td-dev pool; overridable via env). */
export const COGNITO = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "us-east-2_97rx37whe",
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? "5h04udesajaq9u54mrvh2hvttr",
  region: import.meta.env.VITE_COGNITO_REGION ?? "us-east-2",
};

/**
 * AWS WAF CAPTCHA (spam gate on the public submit — spec §NFR-6). The intake form solves a WAF
 * CAPTCHA before POST /api/public/requests; the solved token rides the POST as the `x-aws-waf-token`
 * header (attached by apiFetch — cookie-independent so it works in the cross-site embed iframe) and
 * as the same-origin `aws-waf-token` cookie. Both values are per-ENVIRONMENT and injected at build
 * time:
 *   - VITE_WAF_CAPTCHA_INTEGRATION_URL — the account+region integration host, retrievable via
 *       `aws wafv2 list-api-keys --scope REGIONAL` (field ApplicationIntegrationURL). dev + prod
 *       DIFFER. Trailing slash expected; `jsapi.js` is appended by index.html.
 *   - VITE_WAF_CAPTCHA_API_KEY — the encrypted JS API key (lists the token domain), minted via
 *       `aws wafv2 create-api-key --scope REGIONAL --token-domains <host>`. dev + prod DIFFER.
 * When unset (local dev / mocks / tests) the CAPTCHA is bypassed and the form submits directly —
 * the real WAF only enforces behind CloudFront, so local dev never needs a live challenge.
 */
export const WAF_CAPTCHA = {
  integrationUrl: import.meta.env.VITE_WAF_CAPTCHA_INTEGRATION_URL ?? "",
  apiKey: import.meta.env.VITE_WAF_CAPTCHA_API_KEY ?? "",
};

/** Whether a live WAF CAPTCHA challenge should gate submit (both values present, not mocking). */
export const CAPTCHA_ENABLED: boolean =
  !USE_MOCKS && WAF_CAPTCHA.integrationUrl !== "" && WAF_CAPTCHA.apiKey !== "";
